import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { resolveBannerDraft } from '@/features/location/model/banner-draft'
import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { ImageUploadField } from '@/shared/ui/image-upload-field'

interface LocationBannerDialogProps {
  /** Location name shown in the dialog title. */
  locationName: string
  initialBanner: string | null | undefined
  saving: boolean
  onSave: (banner: string | null) => Promise<void>
  onClose: () => void
  onPreview: (url: string) => void
}

/**
 * Upload / replace / remove the banner of one location. Mount it only while editing so the
 * draft state starts from the stored banner every time.
 */
export function LocationBannerDialog({
  locationName,
  initialBanner,
  saving,
  onSave,
  onClose,
  onPreview,
}: LocationBannerDialogProps) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState(initialBanner ?? '')
  const [uploading, setUploading] = useState(false)
  const { nextBanner, canSave } = resolveBannerDraft(initialBanner, draft, uploading)

  return (
    <Dialog open onOpenChange={(open) => !open && !saving && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('location.bannerDialogTitle', { name: locationName })}</DialogTitle>
          <DialogDescription>{t('location.bannerDialogHint')}</DialogDescription>
        </DialogHeader>

        <ImageUploadField
          label={t('location.banner')}
          value={draft}
          onChange={setDraft}
          onUploadingChange={setUploading}
          onPreview={onPreview}
          onUploadError={(message) => showToast.error(message)}
          disabled={saving}
          buttonSize="sm"
          texts={{
            select: t('location.selectBanner'),
            uploading: t('location.uploadingBanner'),
            empty: t('location.noBanner'),
            previewAlt: t('location.banner'),
          }}
        />

        <DialogFooter>
          {draft.trim() ? (
            <Button
              type="button"
              variant="ghost"
              disabled={saving || uploading}
              onClick={() => setDraft('')}
            >
              {t('location.removeBanner')}
            </Button>
          ) : null}
          <Button type="button" variant="outline" disabled={saving} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={!canSave || saving}
            onClick={() => void onSave(nextBanner)}
          >
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
